import React, { ChangeEvent, FocusEvent, FormEvent } from "react";
import { FaBuilding, FaGlobe, FaUserAlt, FaLock } from "react-icons/fa";
import { BsPersonCircle } from "react-icons/bs";
import { MdEmail } from "react-icons/md";
import { TextInput } from "../common/TextInput";
import Toast from "react-bootstrap/Toast";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import "./Register.scss";
import { COUNTRY_OPTIONS } from "../../constant/Constant";

interface RegisterInputType {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  companyName: string;
  Password: string;
  confirmPassword: string;
  referral: string;
  acceptMarketing: string;
  acceptPrivacy: string;
  country: string;
}

interface TouchedFields {
  [key: string]: boolean;
}

interface ErrorMessages {
  [key: string]: string;
}

interface ShowPasswordType {
  password: boolean;
  confirmPassword: boolean;
}

interface ToastType {
  isShow: boolean;
  header: string;
  msg: string;
  toastType: string;
}

interface RegisterProps {
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  registerInput: RegisterInputType;
  setRegisterInput: React.Dispatch<React.SetStateAction<RegisterInputType>>;
  showPassword: ShowPasswordType;
  setShowPassword: React.Dispatch<React.SetStateAction<ShowPasswordType>>;
  isTouched: TouchedFields;
  setIsTouched: React.Dispatch<React.SetStateAction<TouchedFields>>;
  inputErrorMessage: ErrorMessages;
  setInputErrorMessage: React.Dispatch<React.SetStateAction<ErrorMessages>>;
  acceptMarketing: boolean;
  setAcceptMarketing: React.Dispatch<React.SetStateAction<boolean>>;
  acceptPrivacy: boolean;
  setAcceptPrivacy: React.Dispatch<React.SetStateAction<boolean>>;
  handleEyeClick: (field: keyof ShowPasswordType) => void;
  handleToast: () => void;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleInputBlur: (
    e: FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  toast: ToastType;
}

const Register: React.FC<RegisterProps> = ({
  handleSubmit,
  registerInput,
  setRegisterInput,
  showPassword,
  setShowPassword,
  isTouched,
  setIsTouched,
  inputErrorMessage,
  setInputErrorMessage,
  acceptMarketing,
  setAcceptMarketing,
  acceptPrivacy,
  setAcceptPrivacy,
  handleEyeClick,
  handleToast,
  handleChange,
  handleInputBlur,
  toast,
}) => {
  const getErrorText = (field: keyof RegisterInputType, label: string) => {
    if (isTouched[field] && registerInput[field].trim().length === 0) {
      return `Please enter ${label.toLowerCase()}`;
    }
    return inputErrorMessage[field] || "";
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          <div className="row pl-0">
            <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
              <TextInput
                type="text"
                label="First Name*"
                id="firstName"
                name="firstName"
                icon={<FaUserAlt className="icon" size={22} />}
                value={registerInput.firstName || ""}
                onChange={handleChange}
                onBlur={handleInputBlur}
                className="form-input"
                errorText={getErrorText("firstName", "First Name")}
              />
            </div>

            <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
              <TextInput
                type="text"
                label="Last Name*"
                id="lastName"
                name="lastName"
                icon={<FaUserAlt className="icon" size={22} />}
                value={registerInput.lastName || ""}
                onChange={handleChange}
                onBlur={handleInputBlur}
                errorText={getErrorText("lastName", "Last Name")}
              />
            </div>
          </div>

          <TextInput
            type="text"
            label="Username*"
            id="username"
            name="username"
            icon={<BsPersonCircle className="icon" size={22} />}
            value={registerInput.username || ""}
            onChange={handleChange}
            onBlur={handleInputBlur}
            errorText={getErrorText("username", "Username")}
          />

          <TextInput
            type="text"
            label="Email (Private Domains Only)*"
            id="email"
            name="email"
            icon={<MdEmail className="icon" size={25} />}
            value={registerInput.email || ""}
            onChange={handleChange}
            onBlur={handleInputBlur}
            errorText={getErrorText("email", "Email")}
          />

          <TextInput
            type="text"
            label="Company Name*"
            id="companyName"
            name="companyName"
            icon={<FaBuilding className="icon" size={22} />}
            value={registerInput.companyName || ""}
            onChange={handleChange}
            onBlur={handleInputBlur}
            errorText={getErrorText("companyName", "Company Name")}
          />

          <Form.Group className="form-group country-group">
            <InputGroup>
              <InputGroup.Text>
                <FaGlobe size={20} />
              </InputGroup.Text>
              <Form.Select
                id="country"
                name="country"
                value={registerInput.country || ""}
                onChange={handleChange}
                onBlur={handleInputBlur}
                className={`form-select ${
                  isTouched.country && !registerInput.country
                    ? "is-invalid"
                    : ""
                }`}
              >
                <option value="">Select a country</option>
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
            {isTouched.country && !registerInput.country && (
              <Form.Text className="text-danger">
                Please select a country
              </Form.Text>
            )}
          </Form.Group>

          <TextInput
            type="password"
            label="Password*"
            id="Password"
            name="Password"
            showPassword={showPassword.password}
            handleEyeClick={() => handleEyeClick("password")}
            icon={<FaLock className="icon" size={22} />}
            value={registerInput.Password || ""}
            onChange={handleChange}
            onBlur={handleInputBlur}
            errorText={getErrorText("Password", "Password")}
          />

          <TextInput
            type="password"
            label="Confirm password*"
            id="confirmPassword"
            name="confirmPassword"
            showPassword={showPassword.confirmPassword}
            handleEyeClick={() => handleEyeClick("confirmPassword")}
            icon={<FaLock className="icon" size={22} />}
            value={registerInput.confirmPassword || ""}
            onChange={handleChange}
            onBlur={handleInputBlur}
            errorText={getErrorText("confirmPassword", "Confirm Password")}
          />

          <TextInput
            type="text"
            label="Referral (optional)"
            id="referral"
            name="referral"
            icon={<FaLock className="icon" size={22} />}
            value={registerInput.referral || ""}
            onChange={handleChange}
            onBlur={handleInputBlur}
          />

          <div className="row mt-3">
            <div className="col-12 mb-2">
              <div className="custom-checkbox d-flex align-items-start">
                <input
                  type="checkbox"
                  id="acceptMarketing"
                  checked={acceptMarketing}
                  onChange={(e) => setAcceptMarketing(e.target.checked)}
                  className="checkbox-input"
                />
                <label htmlFor="acceptMarketing" className="checkbox-label">
                  Yes, I would like to receive marketing communications from
                  Nagarro. I can unsubscribe at any time.
                </label>
              </div>
            </div>

            <div className="col-12">
              <div className="custom-checkbox d-flex align-items-start">
                <input
                  type="checkbox"
                  id="acceptPrivacy"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  className="checkbox-input"
                />
                <label htmlFor="acceptPrivacy" className="checkbox-label">
                  I agree to the{" "}
                  <a
                    href="https://www.nagarro.com/en/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Statement
                  </a>{" "}
                  and to the handling of my personal information.
                  <span style={{ color: "red" }}>*</span>
                </label>
              </div>
            </div>
          </div>

          <div className="text-center mt-4">
            <button
              className="btn btn-info waves-effect p-6 secondaryColor"
              type="submit"
              disabled={!acceptPrivacy}
            >
              Sign up <i className="bi bi-box-arrow-in-right"></i>
            </button>
          </div>
        </div>
      </form>

      <Toast
        className="toast-container"
        bg={toast.toastType}
        show={toast.isShow}
        onClose={handleToast}
      >
        {toast.header && (
          <Toast.Header>
            <strong className="me-auto">{toast.header}</strong>
          </Toast.Header>
        )}
        <Toast.Body className="toastBody">{toast.msg}</Toast.Body>
      </Toast>
    </>
  );
};

export default Register;
